import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type Study, type StudyType } from '@/model/Study.model';
import {
  type CollectionStatus,
  type GroupTarget,
  type PlannedAnimalTarget,
  type RegisteredAnimalTarget,
  type SampleDefinitionApiId,
  type SampleDefinitionFormData,
} from '@/model/SamplePlan.model';
import { type TeamGlossary } from '@/model/Glosary.model';
import { type MetadataGlossaryV1 } from '@/model/Metadata.model';
import { _isEmpty, _isNil, _notNil } from '@/littledash';
import { isWriteUserForStudy } from '@/support/auth';
import { RouteService } from '@/support/RouteService';
import { isTempApiId } from '@/utils/ApiId.utils';
import { SamplePlanTable } from '@/components/Samples/SamplePlanTable/SamplePlanTable';
import {
  type SampleDefinitionRowData,
  SamplePlanTableProvider,
  useSamplePlanTableContext,
} from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { samplePlanBulkActions } from '@/components/Samples/SamplePlanTable/SamplePlanTable.utils';
import styles from '@/components/Samples/SamplePlanTable/SamplePlanTable.module.scss';
import SelectDropDown from '@/components/UI/SelectDropDown/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import { useDispatch } from 'react-redux';

/**
 * Bulk actions bar for the V1 sample-plan table. Must be rendered inside a SamplePlanTableProvider.
 * Handles row selection, collection-status polling, and navigation to the sample execute step.
 */
export const SamplePlanTableBulkActionsBar: FC<{
  studyApiId: Study['api_id'];
  studyStatus?: StudyType;
}> = ({ studyApiId, studyStatus }) => {
  const { id: studyId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selection, sample_definition_rows, updateRowSelection } = useSamplePlanTableContext();
  const [collectionStatus, setCollectionStatus] = useState<CollectionStatus | undefined>(undefined);

  const selectedSampleDefinitions = useMemo(
    () =>
      Array.from(selection)
        .map((id) => sample_definition_rows.get(id))
        .filter(_notNil),
    [selection, sample_definition_rows]
  );

  const { invoke: fetchCollectionStatus } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/collection-status',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: { toast: false, capture: true, throw: false, slug: 'sample-plan-collection-status' },
    },
  });

  useEffect(() => {
    if (selection.size === 0 || _isNil(studyApiId)) {
      setCollectionStatus(undefined);
      return;
    }
    const selectedIds = Array.from(selection).filter((id): id is SampleDefinitionApiId => !isTempApiId('sdf', id));
    if (selectedIds.length === 0) {
      setCollectionStatus(undefined);
      return;
    }
    let isCancelled = false;
    fetchCollectionStatus({ path: { studyApiId }, query: { definitions: selectedIds } }).then((response) => {
      if (isCancelled) {
        return;
      }
      setCollectionStatus(
        response.type === 'success' ? (response.body as { status: CollectionStatus }).status : 'unavailable'
      );
    });
    return () => {
      isCancelled = true;
    };
  }, [selection, studyApiId]);

  const navigateToSampleExecuteStepForm = useCallback(
    (selectedSampleDefs: Array<SampleDefinitionRowData>) => {
      const sampleDefinitionIds = selectedSampleDefs.map((def) => def.api_id);
      navigate(RouteService.web({ route: 'studies.samples.execute', path: { id: studyId } }).route, {
        state: { sampleDefinitionIds },
      });
    },
    [navigate, studyId]
  );

  const navigateWithSampleDefinitions = useMemo(
    () => () => navigateToSampleExecuteStepForm(selectedSampleDefinitions),
    [navigateToSampleExecuteStepForm, selectedSampleDefinitions]
  );

  const navigateToSamplesTable = useCallback(
    () => navigate('.', { replace: true, state: { active_tab: 'samples_table' } }),
    [navigate]
  );

  const bulkActions = useMemo(() => {
    const resetSelection = () => updateRowSelection({ type: 'clear-all' });
    return samplePlanBulkActions(
      studyApiId,
      selectedSampleDefinitions,
      resetSelection,
      dispatch,
      navigateWithSampleDefinitions,
      navigateToSamplesTable,
      studyStatus,
      collectionStatus
    );
  }, [
    studyApiId,
    selectedSampleDefinitions,
    updateRowSelection,
    dispatch,
    navigateWithSampleDefinitions,
    navigateToSamplesTable,
    studyStatus,
    collectionStatus,
  ]);

  return (
    <div className="flex items-center">
      {selection.size > 0 && (
        <div className={`${styles.SelectionBadge} mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1`}>
          {selection.size} selected
        </div>
      )}
      <SelectDropDown
        title="Bulk actions"
        disabled={selection.size === 0}
        testId="bulk-actions-dropdown"
        className={`plain f6 ${selection.size > 0 ? 'br--right' : ''}`}
        alignMenu="right"
        btnProps={{ plain: true }}
      >
        <ActionList actions={bulkActions} testPrefix="bulk-actions" />
      </SelectDropDown>
    </div>
  );
};

export interface SamplePlanTableViewContentProps {
  study: Study;
  sampleDefinitions: Array<SampleDefinitionFormData>;
  groups: Array<GroupTarget>;
  registeredAnimals: Array<RegisteredAnimalTarget>;
  plannedAnimals: Array<PlannedAnimalTarget>;
  metadataGlossary: Array<MetadataGlossaryV1>;
  sampleGlossary: Array<TeamGlossary>;
  /** Called when the empty state's "Add sample plan" link is clicked. */
  onAddSamplePlan?: () => void;
}

/**
 * Renders the V1 sample plan overview table with all data provided as props.
 * No data fetching — callers are responsible for loading and passing data.
 * Children are rendered inside the SamplePlanTableProvider, giving them access to table context
 * (e.g. SamplePlanTableBulkActionsBar). The outer component owns the action bar.
 * Used by SamplePlanTableView (V1) and as the "Overview" panel in SamplePlanTableViewV2.
 */
export const SamplePlanTableViewContent: FC<PropsWithChildren<SamplePlanTableViewContentProps>> = ({
  study,
  sampleDefinitions,
  groups,
  registeredAnimals,
  plannedAnimals,
  metadataGlossary,
  sampleGlossary,
  onAddSamplePlan,
  children,
}) => {
  const [selectedSamplePlans, updateSelectedSamplePlans] = useState<Set<SampleDefinitionApiId>>(new Set());

  if (_isEmpty(sampleDefinitions)) {
    return (
      <div className="flex flex-column" data-test-component="SamplePlanTableView" data-test-element="empty-state">
        <div className="flex flex-column items-center bg-light-gray ma4 pa4">
          <div className="f4 lh-copy">No Sample Plan</div>
          <div className="mv4">A preview of your sample plan will appear here</div>
          {isWriteUserForStudy(study) && _notNil(onAddSamplePlan) && (
            <a className="link blue" data-testid="add-sample-plan" onClick={onAddSamplePlan}>
              Add sample plan
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-test-component="SamplePlanTableView" data-test-element="container">
      <SamplePlanTableProvider
        sample_definitions={sampleDefinitions}
        groups={groups}
        registered_animals={registeredAnimals}
        planned_animals={plannedAnimals}
        metadata_glossary={metadataGlossary}
        sample_glossary={sampleGlossary}
        selection={selectedSamplePlans}
        onSelectionChange={(value) => updateSelectedSamplePlans(value as Set<SampleDefinitionApiId>)}
        displaySampleCounts={true}
      >
        {children}
        <SamplePlanTable />
      </SamplePlanTableProvider>
    </div>
  );
};
